import React, { useEffect, useMemo, useState } from 'react';
import {
  changeMyPassword,
  consumeRecoverySessionFromUrl,
  hasRecoveryParamsInUrl,
  isSupabaseAuthConfigured,
  loginWithEmailPassword,
  sendPasswordResetEmail,
  signUpWithEmailPassword,
  logout
} from '../lib/supabaseAuth';
import { upsertMyNickname } from '../lib/supabaseProfile';

function validatePassword(pw) {
  const minLen = 12;
  const hasUpper = /[A-Z]/.test(pw);
  const hasSpecial = /[^A-Za-z0-9]/.test(pw);
  return {
    ok: pw.length >= minLen && hasUpper && hasSpecial,
    minLen,
    hasUpper,
    hasSpecial
  };
}

function getFriendlyAuthError(err, language) {
  const raw = String(err?.message || err || '').trim();
  const fallback = language === 'ro' ? 'A apărut o eroare la autentificare.' : 'Authentication failed.';
  if (!raw) return fallback;

  const parseJson = (value) => {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  let payload = parseJson(raw);
  if (!payload) {
    const s = raw.indexOf('{');
    const e = raw.lastIndexOf('}');
    if (s >= 0 && e > s) payload = parseJson(raw.slice(s, e + 1));
  }

  const code = String(payload?.error_code || payload?.code || '').toLowerCase();
  const message = String(payload?.msg || payload?.error_description || payload?.message || raw);
  const normalized = `${code} ${message}`.toLowerCase();

  if (normalized.includes('invalid_credentials') || normalized.includes('invalid login credentials')) {
    return language === 'ro'
      ? 'Email sau parolă incorecte. Verifică datele și încearcă din nou.'
      : 'Incorrect email or password. Please try again.';
  }
  if (normalized.includes('email_not_confirmed') || normalized.includes('email not confirmed')) {
    return language === 'ro'
      ? 'Contul nu este confirmat. Verifică emailul și confirmă contul.'
      : 'Your account is not confirmed. Please check your email.';
  }
  if (normalized.includes('supabase is not configured')) {
    return language === 'ro'
      ? 'Autentificarea nu este configurată momentan.'
      : 'Authentication is not configured right now.';
  }
  if (normalized.includes('password') && normalized.includes('12')) {
    return language === 'ro'
      ? 'Parola trebuie să respecte regulile minime de securitate.'
      : 'Password does not meet the minimum security rules.';
  }

  if (payload && typeof message === 'string' && message.trim()) return message;
  return fallback;
}

function LoginPage({ language, onLanguageChange, user, profile, onLogin, onBack, onGoHome, onProfileUpdated }) {
  const copy = useMemo(() => {
    if (language === 'ro') {
      return {
        titleIn: 'Autentificare',
        titleUp: 'Creează cont',
        subtitle: 'Intră în ChessMindset Academy pentru progres, lecții și examene.',
        email: 'Email',
        password: 'Parolă',
        confirmPassword: 'Confirmă parola',
        ctaIn: 'Intră',
        ctaUp: 'Creează cont',
        toggleIn: 'Ai deja cont? Intră',
        toggleUp: 'Nu ai cont? Creează cont',
        pwRulesTitle: 'Parola trebuie să aibă:',
        pwRules: 'minim 12 caractere, cel puțin 1 literă mare și 1 caracter special.',
        loading: 'Se încarcă...',
        back: 'Înapoi',
        logout: 'Logout',
        loggedInAs: 'Ești autentificat ca',
        checkEmail: 'Cont creat. Verifică email-ul pentru confirmare, apoi revino aici și intră.',
        forgotPassword: 'Ai uitat parola?',
        resetModalTitle: 'Resetează parola',
        resetModalEmail: 'Adresa de email',
        resetModalSubmit: 'Resetează parola',
        resetModalCancel: 'Anulează',
        resetSending: 'Trimitem emailul...',
        resetSent: 'Am trimis emailul de resetare. Verifică inbox-ul.',
        resetNeedsEmail: 'Completează emailul înainte de resetare.',
        setNewPasswordTitle: 'Setează parola nouă',
        setNewPasswordInfo: 'Introdu parola nouă pentru a finaliza resetarea.',
        setNewPasswordAction: 'Actualizează parola',
        setNewPasswordDone: 'Parola a fost actualizată. Te poți autentifica acum.',
        setNewPasswordInvalid: 'Parola nouă nu respectă regulile de securitate.',
        notConfigured: 'Supabase Auth nu este configurat (lipsesc variabilele din .env).'
      };
    }
    return {
      titleIn: 'Sign in',
      titleUp: 'Create account',
      subtitle: 'Sign in to ChessMindset Academy for progress, lessons, and exams.',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm password',
      ctaIn: 'Sign in',
      ctaUp: 'Create account',
      toggleIn: 'Already have an account? Sign in',
      toggleUp: "Don't have an account? Create one",
      pwRulesTitle: 'Password must have:',
      pwRules: 'at least 12 characters, at least 1 uppercase letter, and 1 special character.',
      loading: 'Loading...',
      back: 'Back',
      logout: 'Sign out',
      loggedInAs: 'Signed in as',
      checkEmail: 'Account created. Check your email to confirm, then come back and sign in.',
      forgotPassword: 'Forgot password?',
      resetModalTitle: 'Reset password',
      resetModalEmail: 'Email address',
      resetModalSubmit: 'Reset password',
      resetModalCancel: 'Cancel',
      resetSending: 'Sending reset email...',
      resetSent: 'Reset email sent. Please check your inbox.',
      resetNeedsEmail: 'Please enter your email first.',
      setNewPasswordTitle: 'Set new password',
      setNewPasswordInfo: 'Enter your new password to complete reset.',
      setNewPasswordAction: 'Update password',
      setNewPasswordDone: 'Password updated. You can sign in now.',
      setNewPasswordInvalid: 'New password does not meet security rules.',
      notConfigured: 'Supabase Auth is not configured (missing .env variables).'
    };
  }, [language]);

  const [mode, setMode] = useState('in'); // 'in' | 'up'
  const [email, setEmail] = useState('');
  const [nickname, setNickname] = useState(() => String(profile?.nickname || '').trim());
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [isRecoveryFlow, setIsRecoveryFlow] = useState(false);
  const configured = isSupabaseAuthConfigured();

  useEffect(() => {
    if (!configured) return;
    const hadRecovery = hasRecoveryParamsInUrl();
    if (!hadRecovery) return;
    const consumed = consumeRecoverySessionFromUrl();
    if (consumed) {
      setMode('in');
      setPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
      setIsRecoveryFlow(true);
      if (window.location.hash !== '#/login?reset=1') {
        window.location.hash = '#/login?reset=1';
      }
    }
  }, [configured]);

  useEffect(() => {
    // Keep nickname input in sync when profile loads/changes.
    setNickname(String(profile?.nickname || '').trim());
  }, [profile?.nickname]);

  const pw = validatePassword(password);
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  return (
    <div className="login-page">
      <div className="login-shell">
        <div className="login-top">
          <div className="login-left">
            <button
              className="login-brand"
              onClick={() => onGoHome && onGoHome()}
              title="ChessMindset.academy"
            >
              Chess<span>Mindset</span>.academy
            </button>

            <button className="login-back" onClick={onBack}>
              ← {copy.back}
            </button>
          </div>
        </div>

        <div className="login-card">
          <div className="login-card-head">
            <h1 className="login-title">
              {isRecoveryFlow ? copy.setNewPasswordTitle : mode === 'up' ? copy.titleUp : copy.titleIn}
            </h1>
            <p className="login-subtitle">{copy.subtitle}</p>
          </div>

          {!configured && (
            <div className="login-warning">
              {copy.notConfigured}
            </div>
          )}

          {user ? (
            <div className="login-authed">
              <div className="login-user">
                <div className="login-user-label">{copy.loggedInAs}</div>
                <div className="login-user-value">{user.email || user.id}</div>
              </div>

              <div style={{ marginTop: 14, width: '100%' }}>
                <label className="auth-label">
                  {language === 'ro' ? 'Alias / Nickname' : 'Nickname'}
                  <input
                    className="auth-input"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder={language === 'ro' ? 'ex: ChessHero' : 'e.g. ChessHero'}
                    autoComplete="nickname"
                  />
                </label>
                <button
                  className="btn-primary"
                  style={{ width: '100%', marginTop: 10 }}
                  disabled={!configured || isSavingProfile || !String(nickname || '').trim()}
                  onClick={async () => {
                    setIsSavingProfile(true);
                    setError('');
                    setSuccess('');
                    try {
                      await upsertMyNickname(nickname);
                      setSuccess(language === 'ro' ? 'Alias salvat.' : 'Nickname saved.');
                      if (typeof onProfileUpdated === 'function') await onProfileUpdated();
                    } catch (e) {
                      setError(getFriendlyAuthError(e, language));
                    } finally {
                      setIsSavingProfile(false);
                    }
                  }}
                >
                  {isSavingProfile ? (language === 'ro' ? 'Salvez...' : 'Saving...') : (language === 'ro' ? 'Salvează alias' : 'Save nickname')}
                </button>
              </div>

              <button
                className="btn-primary"
                onClick={() => {
                  logout();
                  onLogin(null);
                }}
              >
                {copy.logout}
              </button>
            </div>
          ) : (
            <div className="login-form">
              {error && <div className="auth-error">{error}</div>}
              {success && <div className="login-success">{success}</div>}

              {mode === 'up' && (
                <label className="auth-label">
                  {language === 'ro' ? 'Alias / Nickname' : 'Nickname'}
                  <input
                    className="auth-input"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder={language === 'ro' ? 'ex: ChessHero' : 'e.g. ChessHero'}
                    autoComplete="nickname"
                  />
                </label>
              )}

              {!isRecoveryFlow && (
                <label className="auth-label">
                  {copy.email}
                  <input
                    className="auth-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    inputMode="email"
                    placeholder="name@email.com"
                  />
                </label>
              )}

              <label className="auth-label">
                {copy.password}
                <input
                  className="auth-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete={mode === 'up' || isRecoveryFlow ? 'new-password' : 'current-password'}
                  placeholder="••••••••"
                />
              </label>

              {mode === 'in' && !isRecoveryFlow && (
                <button
                  type="button"
                  className="login-toggle"
                  disabled={isLoading || !configured}
                  onClick={() => {
                    setError('');
                    setSuccess('');
                    setResetEmail(String(email || '').trim());
                    setIsResetModalOpen(true);
                  }}
                >
                  {copy.forgotPassword}
                </button>
              )}

              {(mode === 'up' || isRecoveryFlow) && (
                <>
                  <label className="auth-label">
                    {copy.confirmPassword}
                    <input
                      className="auth-input"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••"
                    />
                  </label>

                  <div className="login-pw-rules">
                    <div className="login-pw-rules-title">{copy.pwRulesTitle}</div>
                    <div className="login-pw-rules-text">{copy.pwRules}</div>
                    <div className="login-pw-checks">
                      <span className={pw.minLen ? (password.length >= pw.minLen ? 'ok' : 'bad') : 'bad'}>
                        {password.length >= pw.minLen ? '✓' : '•'} {language === 'ro' ? `${pw.minLen}+ caractere` : `${pw.minLen}+ chars`}
                      </span>
                      <span className={pw.hasUpper ? 'ok' : 'bad'}>
                        {pw.hasUpper ? '✓' : '•'} {language === 'ro' ? '1 literă mare' : '1 uppercase'}
                      </span>
                      <span className={pw.hasSpecial ? 'ok' : 'bad'}>
                        {pw.hasSpecial ? '✓' : '•'} {language === 'ro' ? '1 caracter special' : '1 special'}
                      </span>
                      <span className={passwordsMatch ? 'ok' : 'bad'}>
                        {passwordsMatch ? '✓' : '•'} {language === 'ro' ? 'Parole identice' : 'Passwords match'}
                      </span>
                    </div>
                  </div>
                </>
              )}

              {isRecoveryFlow && (
                <div className="login-warning" style={{ marginBottom: 12 }}>
                  {copy.setNewPasswordInfo}
                </div>
              )}

              <button
                className="btn-primary"
                disabled={
                  isLoading ||
                  !configured ||
                  (!isRecoveryFlow && !email.trim()) ||
                  !password ||
                  ((mode === 'up' || isRecoveryFlow) && (!pw.ok || !passwordsMatch))
                }
                onClick={async () => {
                  setIsLoading(true);
                  setError('');
                  setSuccess('');
                  try {
                    if (isRecoveryFlow) {
                      if (!pw.ok || !passwordsMatch) {
                        throw new Error(copy.setNewPasswordInvalid);
                      }
                      await changeMyPassword(password);
                      setSuccess(copy.setNewPasswordDone);
                      setIsRecoveryFlow(false);
                      setPassword('');
                      setConfirmPassword('');
                      if (window.location.hash !== '#/login') {
                        window.location.hash = '#/login';
                      }
                    } else if (mode === 'up') {
                      const cleanNickname = String(nickname || '').trim();
                      if (!cleanNickname) {
                        throw new Error(language === 'ro' ? 'Te rog alege un alias.' : 'Please choose a nickname.');
                      }
                      const result = await signUpWithEmailPassword(email.trim(), password, {
                        user_type: 'beginner',
                        level: 'beginner',
                        lang: language,
                        nickname: cleanNickname
                      });
                      if (result.session) {
                        onLogin(result.session);
                      } else {
                        setSuccess(copy.checkEmail);
                        setMode('in');
                        setPassword('');
                        setConfirmPassword('');
                        setNickname('');
                      }
                    } else {
                      const session = await loginWithEmailPassword(email.trim(), password);
                      onLogin(session);
                    }
                  } catch (e) {
                    setError(getFriendlyAuthError(e, language));
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                {isLoading
                  ? copy.loading
                  : isRecoveryFlow
                    ? copy.setNewPasswordAction
                    : mode === 'up'
                      ? copy.ctaUp
                      : copy.ctaIn}
              </button>

              {!isRecoveryFlow && (
                <button
                  className="login-toggle"
                  onClick={() => {
                    setError('');
                    setSuccess('');
                    setMode(mode === 'up' ? 'in' : 'up');
                  }}
                >
                  {mode === 'up' ? copy.toggleIn : copy.toggleUp}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {isResetModalOpen && (
        <div className="auth-overlay" role="dialog" aria-modal="true" aria-labelledby="reset-password-title">
          <div className="auth-modal">
            <div className="auth-header">
              <h3 id="reset-password-title">{copy.resetModalTitle}</h3>
              <button
                className="auth-close"
                type="button"
                aria-label={copy.resetModalCancel}
                onClick={() => {
                  if (isResetLoading) return;
                  setIsResetModalOpen(false);
                }}
              >
                ✕
              </button>
            </div>
            <div className="auth-body">
              <label className="auth-label">
                {copy.resetModalEmail}
                <input
                  className="auth-input"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  autoComplete="email"
                  inputMode="email"
                  placeholder="name@email.com"
                />
              </label>
              <button
                type="button"
                className="btn-primary"
                disabled={isResetLoading || !configured}
                onClick={async () => {
                  const safeEmail = String(resetEmail || '').trim();
                  if (!safeEmail) {
                    setError(copy.resetNeedsEmail);
                    setSuccess('');
                    return;
                  }
                  try {
                    setIsResetLoading(true);
                    setError('');
                    setSuccess(copy.resetSending);
                    await sendPasswordResetEmail(safeEmail);
                    setSuccess(copy.resetSent);
                    setIsResetModalOpen(false);
                    setEmail(safeEmail);
                  } catch (e) {
                    setError(getFriendlyAuthError(e, language));
                    setSuccess('');
                  } finally {
                    setIsResetLoading(false);
                  }
                }}
              >
                {isResetLoading ? copy.loading : copy.resetModalSubmit}
              </button>
              <button
                type="button"
                className="login-toggle"
                disabled={isResetLoading}
                onClick={() => setIsResetModalOpen(false)}
              >
                {copy.resetModalCancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginPage;
